<script lang="ts">
  // « Mes tâches » (maquettes Tasks.dc.html and MobileTasks.dc.html): indicators, charts,
  // then « À moi d'agir » grouped by due date, « J'attends une réponse » by how long it has
  // been waiting, and the finished ones. Figures from /api/me/tasks/summary.
  import { onMount } from 'svelte';
  import { ApiError, type Message } from '../lib/api';
  import { router, threadPath } from '../lib/router.svelte';
  import { app } from '../lib/stores.svelte';
  import { businessDaysSince, doneText, dueGroup, shortDate, sinceText, STATUS_LABEL, todayIso } from '../lib/tasks';

  let error = $state('');
  let view = $state<'open' | 'done'>('open');

  onMount(() => {
    Promise.all([app.loadMyTasks(), app.loadTaskSummary()]).catch(
      (e) => (error = e instanceof ApiError ? e.message : 'Impossible de charger vos tâches'),
    );
  });

  const s = $derived(app.taskSummary);
  const mine = $derived(app.myTasks);
  const projectName = (m: Message) => {
    const c = app.conversation(m.conversation_id);
    return c ? `#${c.title}` : '';
  };
  const today = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date());

  const GROUPS = [
    { key: 'late', label: 'En retard' },
    { key: 'today', label: "Aujourd'hui" },
    { key: 'week', label: 'Cette semaine' },
    { key: 'later', label: 'Plus tard' },
    { key: 'none', label: 'Sans échéance' },
  ] as const;
  const groups = $derived(
    GROUPS.map((g) => ({ ...g, tasks: (mine?.my_turn ?? []).filter((m) => m.task && dueGroup(m.task) === g.key) })).filter(
      (g) => g.tasks.length > 0,
    ),
  );

  const oldest = $derived(s?.waiting_others_oldest_since ? sinceText(s.waiting_others_oldest_since) : '');
  const overdueSince = $derived.by(() => {
    const late = (mine?.my_turn ?? []).filter((m) => m.task?.due_date && m.task.due_date < todayIso());
    const first = late.map((m) => m.task!.due_date!).sort()[0];
    return first ? `depuis ${shortDate(first)}` : '';
  });
  const todayDoing = $derived((mine?.my_turn ?? []).filter((m) => m.task?.due_date === todayIso() && m.task.status === 'doing').length);

  /** Bars: late, then today and the 6 following days. */
  const days = $derived.by(() => {
    if (!s) return [];
    const bars = [{ label: 'Retard', count: s.overdue, kind: 'late' }];
    s.by_day.forEach((d, i) => {
      const label = i === 0 ? 'Auj.' : new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric' }).format(new Date(`${d.date}T12:00:00`));
      bars.push({ label, count: d.count, kind: i === 0 ? 'today' : 'day' });
    });
    return bars;
  });
  const maxDay = $derived(Math.max(1, ...days.map((d) => d.count)));
  const maxProject = $derived(Math.max(1, ...(s?.by_project ?? []).map((p) => p.my_turn)));

  // Mobile ring: share of « à faire » and « en cours ».
  const ring = $derived.by(() => {
    if (!s || s.my_turn === 0) return { todo: 0, doing: 0 };
    const c = 2 * Math.PI * 42;
    return { todo: (s.my_turn_todo / s.my_turn) * c, doing: (s.my_turn_doing / s.my_turn) * c, c };
  });

  const open = (m: Message) => router.go(threadPath(m.conversation_id, m.id));
  const metaOf = (m: Message) => {
    const t = m.task!;
    const parts: string[] = [];
    if (t.requester.id !== app.me?.id) parts.push(`demandée par ${t.requester.display_name}`);
    if (m.reply_count) parts.push(`${m.reply_count} commentaire${m.reply_count > 1 ? 's' : ''}`);
    if (t.document_count) parts.push(`${t.document_count} document${t.document_count > 1 ? 's' : ''}`);
    return parts.join(' · ') || 'aucun commentaire';
  };
</script>

{#snippet row(m: Message, mode: 'mine' | 'waiting' | 'done')}
  {@const t = m.task!}
  <li class="row">
    {#if mode === 'waiting' && t.waiting_on}
      <span class="pill waiting">Main : {t.waiting_on.display_name.split(' ')[0]}</span>
    {:else if mode === 'mine'}
      <!-- My turn: the stored status (« En attente » would be about someone else). -->
      <span class="pill {t.status}">{STATUS_LABEL[t.status]}</span>
    {:else}
      <span class="pill {t.display_status}">{STATUS_LABEL[t.display_status]}</span>
    {/if}
    <a
      href={threadPath(m.conversation_id, m.id)}
      class="row-text"
      onclick={(e) => {
        e.preventDefault();
        open(m);
      }}
    >
      <span class="row-title">{t.title}</span>
      <span class="row-meta">{projectName(m)} · {mode === 'waiting' ? `${t.requester.id === app.me?.id ? 'demandée par moi' : `demandée par ${t.requester.display_name}`}${t.assignee ? ` · responsable ${t.assignee.display_name}` : ''}` : metaOf(m)}</span>
    </a>
    {#if mode === 'waiting'}
      <span class="row-due" class:stale={!!t.waiting_since && businessDaysSince(t.waiting_since) >= 2}>{sinceText(t.waiting_since)}</span>
    {:else if mode === 'done'}
      <span class="row-due">{t.done_at ? doneText(t.done_at) : ''}</span>
    {:else}
      <span class="row-due" class:late={!!t.due_date && t.due_date < todayIso()}>
        {t.due_date ? (t.due_date === todayIso() ? "aujourd'hui" : shortDate(t.due_date)) : '—'}
      </span>
    {/if}
  </li>
{/snippet}

<section class="page" aria-labelledby="tasks-title">
  <header>
    <button type="button" class="back" aria-label="Retour aux conversations" onclick={() => router.go('/')}>
      <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
    </button>
    <div class="title">
      <h1 id="tasks-title">Mes tâches</h1>
      <p class="sub"><span class="cap">{today}</span> · tous les projets</p>
    </div>
    <div class="switch" role="group" aria-label="Affichage">
      <button type="button" aria-pressed={view === 'open'} onclick={() => (view = 'open')}>Ouvertes</button>
      <button type="button" aria-pressed={view === 'done'} onclick={() => (view = 'done')}>Terminées</button>
    </div>
  </header>

  <div class="column">
    {#if error}<p class="error" role="alert">{error}</p>{/if}
    {#if !s || !mine}
      <p class="muted">Chargement…</p>
    {:else if view === 'done'}
      <section class="group">
        <h2>Terminées depuis 30 jours <span class="badge">{mine.done.length}</span></h2>
        {#if mine.done.length}
          <ul class="list">{#each mine.done as m (m.id)}{@render row(m, 'done')}{/each}</ul>
        {:else}<p class="muted">Aucune tâche terminée ces 30 derniers jours.</p>{/if}
      </section>
    {:else}
      <!-- Indicators -->
      <section class="cards" aria-label="Résumé">
        <div class="card navy">
          <span class="label">À moi d'agir</span>
          <span class="big">{s.my_turn}</span>
          <span class="small">{s.my_turn_doing} en cours · {s.my_turn_todo} à faire</span>
          <span class="accent-bar" style:width="{s.my_turn ? Math.round((s.my_turn_doing / s.my_turn) * 100) : 0}%" aria-hidden="true"></span>
        </div>
        <div class="card late">
          <span class="label">En retard</span>
          <span class="big">{s.overdue}</span>
          <span class="small">{overdueSince || 'rien en retard'}</span>
        </div>
        <div class="card">
          <span class="label">Pour aujourd'hui</span>
          <span class="big">{s.due_today}</span>
          <span class="small">{todayDoing ? `dont ${todayDoing} en cours` : '—'}</span>
        </div>
        <div class="card waiting">
          <span class="label">J'attends une réponse</span>
          <span class="big">{s.waiting_others}</span>
          <span class="small">{oldest ? `la plus ancienne ${oldest}` : 'personne à relancer'}</span>
        </div>
      </section>

      <!-- Mobile summary: ring, three pills, 7 days -->
      <section class="mobile-summary" aria-label="Résumé">
        <svg role="img" aria-label="{s.my_turn} tâches où vous avez la main : {s.my_turn_todo} à faire, {s.my_turn_doing} en cours" width="104" height="104" viewBox="0 0 104 104">
          <circle cx="52" cy="52" r="42" fill="none" stroke="var(--status-todo-soft)" stroke-width="12" />
          {#if ring.c}
            <circle cx="52" cy="52" r="42" fill="none" stroke="var(--status-todo)" stroke-width="12" stroke-dasharray="{ring.todo} {ring.c}" transform="rotate(-90 52 52)" />
            <circle cx="52" cy="52" r="42" fill="none" stroke="var(--status-doing)" stroke-width="12" stroke-dasharray="{ring.doing} {ring.c}" stroke-dashoffset={-ring.todo} transform="rotate(-90 52 52)" />
          {/if}
          <text x="52" y="58" text-anchor="middle" font-size="24" font-weight="700" fill="var(--ink)">{s.my_turn}</text>
        </svg>
        <div class="mobile-pills">
          <span class="mp late"><strong>{s.overdue}</strong> en retard</span>
          <span class="mp"><strong>{s.due_today}</strong> aujourd'hui</span>
          <span class="mp waiting"><strong>{s.waiting_others}</strong> j'attends</span>
        </div>
      </section>

      <!-- Charts -->
      <section class="charts" aria-label="Graphiques">
        <div class="chart">
          <h2>Par statut, à moi d'agir</h2>
          <div class="stack" role="img" aria-label="{s.my_turn_todo} à faire, {s.my_turn_doing} en cours">
            <span style:flex={s.my_turn_todo} style:background="var(--status-todo)"></span>
            <span style:flex={s.my_turn_doing} style:background="var(--status-doing)"></span>
          </div>
          <div class="legend">
            <span><i style:background="var(--status-todo)"></i>À faire<b>{s.my_turn_todo}</b></span>
            <span><i style:background="var(--status-doing)"></i>En cours<b>{s.my_turn_doing}</b></span>
            <span class="muted"><i style:background="var(--status-done)"></i>Terminées cette semaine<b>{s.done_this_week}</b></span>
          </div>
        </div>
        <div class="chart">
          <h2>Échéances des 7 prochains jours</h2>
          <div class="bars" role="img" aria-label={days.filter((d) => d.count).map((d) => `${d.count} ${d.label}`).join(', ') || 'aucune échéance'}>
            {#each days as d (d.label)}
              <div class="bar-col">
                <span class="bar-n" class:late={d.kind === 'late'}>{d.count || ''}</span>
                <span class="bar {d.kind}" class:empty={!d.count} style:height="{d.count ? Math.max(8, (d.count / maxDay) * 72) : 3}px"></span>
                <span class="bar-label" class:strong={d.kind !== 'day'}>{d.label}</span>
              </div>
            {/each}
          </div>
        </div>
        <div class="chart">
          <h2>Par projet, à moi d'agir</h2>
          {#each s.by_project as p (p.conversation_id)}
            <div class="proj">
              <div class="proj-head"><span>{p.name}</span><b>{p.my_turn}</b></div>
              <div class="proj-bar"><span style:width="{(p.my_turn / maxProject) * 100}%"></span></div>
            </div>
          {:else}
            <p class="muted">Aucune tâche où vous avez la main.</p>
          {/each}
        </div>
      </section>

      <!-- Lists -->
      {#each groups as g (g.key)}
        <section class="group">
          <h2 class={g.key === 'late' ? 'late' : ''}>{g.label} <span class="badge">{g.tasks.length}</span></h2>
          <ul class="list">{#each g.tasks as m (m.id)}{@render row(m, 'mine')}{/each}</ul>
        </section>
      {:else}
        <p class="muted">Rien à faire de votre côté pour l'instant.</p>
      {/each}
      {#if mine.waiting_others.length}
        <section class="group">
          <h2 class="waiting">J'attends une réponse <span class="badge">{mine.waiting_others.length}</span></h2>
          <ul class="list">{#each mine.waiting_others as m (m.id)}{@render row(m, 'waiting')}{/each}</ul>
        </section>
      {/if}
    {/if}
  </div>
</section>

<style>
  .page {
    height: 100%;
    overflow-y: auto;
    background: var(--bg-page);
  }
  header {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    min-height: 72px;
    padding: 12px 32px;
    border-bottom: 1px solid var(--border);
    background: var(--bg);
  }
  .title {
    flex: 1;
    min-width: 200px;
  }
  h1 {
    margin: 0;
    font-size: 20px;
    line-height: 28px;
  }
  .sub {
    margin: 0;
    font-size: 13px;
    color: var(--muted);
  }
  .cap::first-letter {
    text-transform: uppercase;
  }
  .back {
    display: none;
    width: 44px;
    height: 44px;
    margin-left: -12px;
    border: 0;
    background: transparent;
    color: var(--accent-text);
    align-items: center;
    justify-content: center;
  }
  .switch {
    display: flex;
    padding: 3px;
    background: var(--bg-page);
    border-radius: 8px;
  }
  .switch button {
    height: 30px;
    padding: 0 14px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: var(--muted);
  }
  .switch button[aria-pressed='true'] {
    background: var(--bg);
    color: var(--ink);
    font-weight: 600;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  }
  .column {
    padding: 24px 32px 40px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    max-width: 1080px;
  }
  .muted {
    color: var(--muted);
    margin: 0;
  }
  .cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 16px;
  }
  .card {
    padding: 16px 20px;
    border-radius: 12px;
    background: var(--bg);
    border: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .card .label {
    font-size: 13px;
    font-weight: 500;
    color: var(--muted);
  }
  .card .big {
    font-size: 40px;
    line-height: 44px;
    font-weight: 700;
  }
  .card .small {
    font-size: 12px;
    color: var(--muted);
  }
  .card.navy {
    background: var(--navy);
    border-color: var(--navy);
    color: #ffffff;
  }
  .card.navy .label,
  .card.navy .small {
    color: #ffffff;
    opacity: 0.9;
  }
  .accent-bar {
    height: 4px;
    border-radius: 2px;
    background: var(--accent);
    margin-top: 6px;
    min-width: 4px;
  }
  .card.late {
    background: var(--danger-soft);
    border-color: var(--danger-soft);
  }
  .card.late .label,
  .card.late .small,
  .card.late .big {
    color: var(--danger-text);
  }
  .card.late .big {
    color: var(--danger);
  }
  .card.waiting {
    background: var(--status-waiting-soft);
    border-color: var(--status-waiting-soft);
  }
  .card.waiting .label,
  .card.waiting .small,
  .card.waiting .big {
    color: var(--status-waiting-text);
  }
  .mobile-summary {
    display: none;
  }
  .charts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 16px;
  }
  .chart {
    padding: 16px 20px 20px;
    border-radius: 12px;
    border: 1px solid var(--border);
    background: var(--bg);
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .chart h2 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
  }
  .stack {
    display: flex;
    height: 14px;
    border-radius: 7px;
    overflow: hidden;
    gap: 2px;
    background: var(--status-todo-soft);
  }
  .legend {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .legend span {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .legend i {
    width: 10px;
    height: 10px;
    border-radius: 3px;
  }
  .legend b {
    margin-left: auto;
  }
  .bars {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    height: 104px;
  }
  .bar-col {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }
  .bar-n {
    font-size: 12px;
    font-weight: 600;
  }
  .bar-n.late {
    color: var(--danger);
  }
  .bar {
    width: 100%;
    max-width: 28px;
    border-radius: 4px;
    background: var(--status-todo);
  }
  .bar.late {
    background: var(--danger);
  }
  .bar.today {
    background: var(--navy);
  }
  .bar.empty {
    background: var(--border);
  }
  .bar-label {
    font-size: 11px;
    line-height: 14px;
    color: var(--muted);
    white-space: nowrap;
  }
  .bar-label.strong {
    color: var(--ink);
    font-weight: 600;
  }
  .proj {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .proj-head {
    display: flex;
    gap: 8px;
  }
  .proj-head span {
    flex: 1;
  }
  .proj-bar {
    height: 8px;
    border-radius: 4px;
    background: var(--status-todo-soft);
    overflow: hidden;
  }
  .proj-bar span {
    display: block;
    height: 8px;
    border-radius: 4px;
    background: var(--accent);
  }
  .group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .group h2 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
  }
  .group h2.late {
    color: var(--danger);
  }
  .group h2.waiting {
    color: var(--status-waiting-text);
  }
  .badge {
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background: var(--status-todo-soft);
    font-size: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .list {
    list-style: none;
    margin: 0;
    padding: 0;
    border: 1px solid var(--border);
    border-radius: 10px;
    overflow: hidden;
    background: var(--bg);
  }
  .row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-top: 1px solid var(--border);
  }
  .row:first-child {
    border-top: 0;
  }
  .row .pill {
    flex: 0 0 auto;
    height: 26px;
    padding: 0 10px;
    border-radius: 13px;
  }
  .row-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    color: var(--ink);
    text-decoration: none;
  }
  .row-text:hover .row-title {
    text-decoration: underline;
  }
  .row-title {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .row-meta {
    font-size: 12px;
    line-height: 16px;
    color: var(--muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .row-due {
    flex: 0 0 auto;
    font-size: 13px;
    color: var(--ink-2);
  }
  .row-due.late {
    color: var(--danger);
    font-weight: 600;
  }
  .row-due.stale {
    color: var(--accent-text);
    font-weight: 600;
  }
  @media (max-width: 759px) {
    header {
      padding: 8px 12px;
    }
    .back {
      display: flex;
    }
    .column {
      padding: 12px;
      gap: 16px;
    }
    .cards,
    .charts > .chart:first-child,
    .charts > .chart:last-child {
      display: none;
    }
    .mobile-summary {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 16px;
      border-radius: 12px;
      background: var(--bg);
      border: 1px solid var(--border);
    }
    .mobile-pills {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .mp {
      font-size: 13px;
      color: var(--ink-2);
    }
    .mp.late {
      color: var(--danger);
    }
    .mp.waiting {
      color: var(--status-waiting-text);
    }
    .row {
      padding: 12px;
    }
  }
</style>
